/* eslint-disable no-console */
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useAuthContext } from "../../context/auth";
import { getCreditos } from "../../utils/creditos";
// DEPRECADO (cards de resumen removidos 2026-08-16): reactivar junto con `resumenGlobal` cuando
// se rediseñe la página. La tabla no usa conversión — muestra montos en su moneda original.
// import { convertirAMonedaLocal } from "../../utils/exchangeRateService";
import { useCurrency } from "../../utils/useCurrency";
import { Header } from "../Header";
import { Loader } from "../Loader";
import CrearCreditoModal from "./CrearCreditoModal";
import DetalleCreditoModal from "./DetalleCreditoModal";
import styles from "./Creditos.module.scss";

const FILTRO_OPCIONES = [
  { value: "activos", label: "Activos" },
  { value: "liquidados", label: "Liquidados" },
  { value: "todos", label: "Todos" },
];

const TIPO_ACREEDOR_LABEL = {
  banco: "Banco",
  privado: "Privado",
  casa_matriz: "Casa matriz",
  intercompany: "Intercompany",
  socio: "Socio",
  contraparte: "Contraparte",
};

// Etiqueta visible según la dirección de la obligación (se compara por el campo `direccion`,
// nunca por nombre — regla 10.7). Cualquier valor distinto de 'otorgado' se trata como deuda.
const esOtorgado = (credito) => credito?.direccion === "otorgado";

const formatFechaCorta = (fechaStr) => {
  if (!fechaStr) return "—";
  const f = fechaStr.includes("T") ? fechaStr.substring(0, 10) : fechaStr;
  const [, m, d] = f.split("-");
  return `${d}/${m}`;
};

// Métricas derivadas de las cuotas de un crédito.
const calcularResumenCredito = (credito) => {
  const cuotas = credito.cuotas || [];
  const pagadas = cuotas.filter((c) => c.estado === "pagada");
  const pendientes = cuotas.filter((c) => c.estado !== "pagada");
  const saldoPendiente = pendientes.reduce((acc, c) => acc + Number(c.monto_total || 0), 0);
  const fechasPendientes = pendientes.map((c) => c.fecha_vencimiento);
  fechasPendientes.sort((a, b) => (a || "").localeCompare(b || ""));
  const proximaCuota = fechasPendientes[0];
  // Monto de cuota "típico": fijo si existe, si no la primera cuota fuera de gracia.
  const tieneCuotaFija = credito.monto_cuota_fijo != null && credito.monto_cuota_fijo !== "";
  const primeraConMonto = pendientes.find((c) => Number(c.monto_total) > 0);
  const montoVariable = primeraConMonto?.monto_total ?? cuotas[0]?.monto_total ?? 0;
  const montoCuota = tieneCuotaFija ? Number(credito.monto_cuota_fijo) : montoVariable;
  return {
    cuotasPagadas: pagadas.length,
    cuotasTotal: cuotas.length,
    saldoPendiente,
    proximaCuota,
    montoCuota,
  };
};

const Creditos = () => {
  const { pais, id: userId } = useAuthContext();
  const { formatAmount } = useCurrency();

  const [creditos, setCreditos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState("activos");
  const [crearModal, setCrearModal] = useState(false);
  const [detalleId, setDetalleId] = useState(null);
  const [editCredito, setEditCredito] = useState(null); // solo 'otorgado' — edición a favor

  const cargar = async () => {
    if (!pais) return;
    setLoading(true);
    try {
      const data = await getCreditos(pais);
      setCreditos(data);
    } catch (err) {
      console.error("Error cargando créditos:", err);
      toast.error("Error cargando créditos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pais]);

  const creditosFiltrados = useMemo(() => {
    if (filtro === "todos") return creditos;
    const estado = filtro === "liquidados" ? "liquidado" : "activo";
    return creditos.filter((c) => c.estado === estado);
  }, [creditos, filtro]);

  const detalleCredito = useMemo(() => creditos.find((c) => c.id === detalleId) || null, [creditos, detalleId]);

  // ── DEPRECADO (2026-08-16): cálculo de los totales de resumen que alimentaban los cards de
  // arriba (Créditos activos / Por pagar / Por cobrar / Liquidados / Total). Los cards se quitaron
  // del render por ahora; Ignacio rediseña la página. NO se borra (regla 7): reactivar tal cual al
  // reponer los cards, junto con el import de `convertirAMonedaLocal` y `loadingRates` de useCurrency.
  //   · porPagar  = deuda propia (direccion='tomado')     → lo que debemos
  //   · porCobrar = a favor      (direccion='otorgado')   → lo que tenemos a recuperar
  // Multimoneda (10.185): cada cuota pendiente se convierte a moneda local por su
  // fecha_vencimiento (CLP/PEN local = identity). Se gateaba en loadingRates para no sumar con
  // tasas sin cargar. Filtro por país ya viene aplicado desde getCreditos(pais).
  //
  // const monedaLocal = pais === "Perú" ? "PEN" : "CLP";
  // const resumenGlobal = useMemo(() => {
  //   let porPagar = 0;
  //   let porCobrar = 0;
  //   const activos = creditos.filter((c) => c.estado === "activo");
  //   if (!loadingRates) {
  //     activos.forEach((c) => {
  //       const pendientes = (c.cuotas || []).filter((q) => q.estado !== "pagada");
  //       const saldoLocal = pendientes.reduce(
  //         (acc, q) => acc + convertirAMonedaLocal(Number(q.monto_total || 0), c.moneda, pais, q.fecha_vencimiento),
  //         0
  //       );
  //       if (esOtorgado(c)) porCobrar += saldoLocal;
  //       else porPagar += saldoLocal;
  //     });
  //   }
  //   return {
  //     total: creditos.length,
  //     activos: activos.length,
  //     liquidados: creditos.filter((c) => c.estado === "liquidado").length,
  //     porPagar,
  //     porCobrar,
  //   };
  // }, [creditos, pais, loadingRates]);

  return (
    <div className={styles.wrapper}>
      <Header
        centerContent={
          <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "center" }}>
            <select
              className="form-select form-select-sm"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              style={{ width: 160, fontSize: "0.8rem", textAlign: "left" }}
              title="Filtro de estado"
            >
              {FILTRO_OPCIONES.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        }
      >
        <button type="button" className={styles.btnNuevo} onClick={() => setCrearModal(true)}>
          + Nuevo crédito
        </button>
      </Header>

      <div className={styles.content}>
        {loading ? (
          <Loader />
        ) : (
          <>
            {/* Cards de resumen removidos 2026-08-16 (rediseño pendiente); solo filtro + tabla.
                La lógica de los totales queda deprecada arriba (`resumenGlobal`) para reponerlos. */}
            {creditosFiltrados.length === 0 ? (
              <div className={styles.empty}>Sin créditos registrados.</div>
            ) : (
              <div className={styles.tableContainer}>
                <div className={styles.tableResponsive}>
                  <table className={`table table-hover ${styles.table}`}>
                    <thead>
                      <tr>
                        <th className={styles.colNombre}>Nombre</th>
                        <th className={styles.colDireccion}>Dirección</th>
                        <th className={styles.colAcreedor}>Acreedor</th>
                        <th className={styles.colTipo}>Tipo</th>
                        <th className={styles.colMonto}>Monto original</th>
                        <th className={styles.colMoneda}>Moneda</th>
                        <th className={styles.colCuotas}>Cuotas</th>
                        <th className={styles.colCuota}>Monto cuota</th>
                        <th className={styles.colProxima}>Próxima</th>
                        <th className={styles.colSaldo}>Saldo pend.</th>
                        <th className={styles.colEstado}>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {creditosFiltrados.map((c) => {
                        const r = calcularResumenCredito(c);
                        return (
                          <tr key={c.id} onClick={() => setDetalleId(c.id)}>
                            <td className={styles.colNombre} title={c.nombre}>
                              {c.nombre}
                            </td>
                            <td className={styles.colDireccion}>
                              <span
                                className={`${styles.estadoBadge} ${
                                  esOtorgado(c) ? styles.dirAFavor : styles.dirTomado
                                }`}
                              >
                                {esOtorgado(c) ? "A favor" : "Tomado"}
                              </span>
                            </td>
                            <td className={styles.colAcreedor} title={c.acreedor_nombre}>
                              {c.acreedor_nombre}
                            </td>
                            <td className={styles.colTipo}>
                              {TIPO_ACREEDOR_LABEL[c.tipo_acreedor] || c.tipo_acreedor}
                            </td>
                            <td className={styles.colMonto}>{formatAmount(c.monto_original, c.moneda)}</td>
                            <td className={styles.colMoneda}>{c.moneda}</td>
                            <td className={styles.colCuotas}>
                              {r.cuotasPagadas}/{r.cuotasTotal}
                            </td>
                            <td className={styles.colCuota}>{formatAmount(r.montoCuota, c.moneda)}</td>
                            <td className={styles.colProxima}>{formatFechaCorta(r.proximaCuota)}</td>
                            <td className={styles.colSaldo}>{formatAmount(r.saldoPendiente, c.moneda)}</td>
                            <td className={styles.colEstado}>
                              <span
                                className={`${styles.estadoBadge} ${
                                  c.estado === "liquidado" ? styles.estadoLiquidado : styles.estadoActivo
                                }`}
                              >
                                {c.estado === "liquidado" ? "Liquidado" : "Activo"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <CrearCreditoModal
        isOpen={crearModal || !!editCredito}
        onClose={() => {
          setCrearModal(false);
          setEditCredito(null);
        }}
        paisDefault={pais}
        userId={userId}
        onSuccess={cargar}
        editCredito={editCredito}
      />
      <DetalleCreditoModal
        credito={detalleCredito}
        onClose={() => setDetalleId(null)}
        onRefresh={cargar}
        onEdit={(c) => {
          setDetalleId(null);
          setEditCredito(c);
        }}
      />
    </div>
  );
};

export default Creditos;
